import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { RealtimeChannel } from '@supabase/supabase-js'

export interface Notification {
  id: string
  type: string
  title: string
  message: string
  link?: string
  read_at?: string
  created_at: string
  metadata: Record<string, any>
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setNotifications([]); setUnreadCount(0); return }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      const list = (data || []) as Notification[]
      setNotifications(list)
      setUnreadCount(list.filter(n => !n.read_at).length)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase.functions.invoke('mark-notification-read', { body: { notificationId } })
      if (error) throw error
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      setError(err as Error)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      const { error } = await supabase.functions.invoke('mark-all-notifications-read')
      if (error) throw error
      const now = new Date().toISOString()
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || now })))
      setUnreadCount(0)
    } catch (err) {
      setError(err as Error)
    }
  }, [])

  useEffect(() => {
    loadNotifications()

    let channel: RealtimeChannel | undefined
    const setup = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      channel = supabase
        .channel(`notifications:${user.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        }, (payload) => {
          const n = payload.new as Notification
          setNotifications(prev => [n, ...prev])
          setUnreadCount(prev => prev + 1)
        })
        .subscribe()
    }
    setup()

    return () => { channel?.unsubscribe() }
  }, [loadNotifications])

  return { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, refresh: loadNotifications }
}


